import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PROJECT_HOURS_PDF_MAX_IDS,
  projectHoursPdfFallbackFilename,
  requestProjectHoursPdf,
  saveBlobAsFile,
} from "../../../api/project";
import { exportHoursPdf } from "../../../api/hour";
import { normalizeError } from "../../../operationFeedback/normalizeError";

/**
 * Map a failed PDF export to inline Operation Feedback for the time overview.
 * Returns { message, recovery } where recovery is "reload" (refresh the list),
 * "edit" (narrow the selection) or "none".
 */
export function projectHoursPdfFeedback(error, ui) {
  if (error?.code === "PROJECT_HOURS_PDF_TOO_MANY_CLIENT") {
    return { message: ui("pdf_too_many", "Utvalget er for stort for PDF (maks {{max}} føringer). Velg et mindre utvalg.", { max: PROJECT_HOURS_PDF_MAX_IDS }), recovery: "edit" };
  }
  if (error?.code === "PROJECT_HOURS_PDF_INVALID_RESPONSE") {
    return { message: ui("pdf_invalid", "Serveren returnerte ikke en gyldig PDF. Prøv igjen."), recovery: "none" };
  }
  const normalized = normalizeError(error);
  if (
    normalized.httpStatus === 409 ||
    normalized.code === "PROJECT_HOURS_PDF_SELECTION_CHANGED" ||
    normalized.code === "HOURS_EXPORT_SELECTION_CHANGED"
  ) {
    return { message: ui("pdf_selection_changed", "Timelisten er endret siden den ble lastet. Oppdater listen og prøv igjen."), recovery: "reload" };
  }
  if (normalized.code === "PROJECT_HOURS_PDF_TOO_MANY" || /export_too_many|too many|for mange/i.test(String(error?.message || ""))) {
    return { message: ui("pdf_too_many", "Utvalget er for stort for PDF (maks {{max}} føringer). Velg et mindre utvalg.", { max: PROJECT_HOURS_PDF_MAX_IDS }), recovery: "edit" };
  }
  if (normalized.httpStatus === 403) {
    return { message: ui("pdf_forbidden", "Du har ikke tilgang til å eksportere timene i dette prosjektet."), recovery: "none" };
  }
  const hasServerMessage = normalized.httpStatus && normalized.httpStatus < 500 && normalized.code !== "UNKNOWN";
  return {
    message: hasServerMessage ? normalized.safeMessage : ui("pdf_failed", "Kunne ikke lage PDF. Prøv igjen."),
    recovery: "none",
  };
}

/**
 * Export the currently filtered project hours as an API-rendered PDF.
 * Presentation (busy state + inline feedback) is owned by the caller.
 *
 * Every export is an "attempt". Changing the selection (project, entries or
 * period) starts a new attempt, so a request still in flight for the previous
 * selection can neither show its error nor clear the busy state of a newer one.
 * A successful file is still saved: it holds exactly the hours that were exported.
 */
export function useProjectHoursPdfExport({
  projectId,
  projectNumber,
  entries,
  dateFrom,
  dateTo,
  language,
  ui,
  companyScope = false,
  exportFilters = [],
}) {
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const mounted = useRef(false);
  const attempt = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => {
    // Re-arm on every mount (React StrictMode mounts, unmounts and remounts).
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Content key, not array identity: a background reload with the same hours
  // is not a new selection.
  const selectionKey = useMemo(() => JSON.stringify([
    companyScope ? "company" : (projectId ?? null),
    dateFrom ?? null,
    dateTo ?? null,
    exportFilters,
    (entries || []).map((entry) => entry?.id).filter((id) => id != null),
  ]), [companyScope, projectId, entries, dateFrom, dateTo, exportFilters]);

  // A stale error about a previous selection is misleading once the selection changes.
  // exportPdf records the selection it ran for, so an effect that flushes after
  // the click for that same selection does not invalidate the new attempt.
  const attemptSelection = useRef(selectionKey);
  useEffect(() => {
    if (attemptSelection.current === selectionKey) return;
    attemptSelection.current = selectionKey;
    attempt.current += 1;
    busyRef.current = false;
    setBusy(false);
    setFeedback(null);
  }, [selectionKey]);

  const exportPdf = useCallback(async () => {
    if (busyRef.current) return false;
    if (companyScope) {
      const hourIds = (entries || []).map((entry) => entry?.id).filter((id) => id != null);
      attemptSelection.current = selectionKey;
      attempt.current += 1;
      const myAttempt = attempt.current;
      const isCurrent = () => mounted.current && attempt.current === myAttempt;
      setFeedback(null);
      if (hourIds.length === 0) {
        setFeedback({
          message: ui("pdf_empty", "Ingen timer å eksportere i utvalget."),
          recovery: "edit",
        });
        return false;
      }
      if (hourIds.length > PROJECT_HOURS_PDF_MAX_IDS) {
        const error = new Error("too many");
        error.code = "PROJECT_HOURS_PDF_TOO_MANY_CLIENT";
        setFeedback(projectHoursPdfFeedback(error, ui));
        return false;
      }
      busyRef.current = true;
      setBusy(true);
      try {
        // hour_ids pin the export to the visible (client-filtered) rows so status /
        // type / link chips match the PDF — same contract as project export.
        await exportHoursPdf({
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          filters: exportFilters,
          hour_ids: hourIds,
        });
        return true;
      } catch (error) {
        if (isCurrent()) setFeedback(projectHoursPdfFeedback(error, ui));
        return false;
      } finally {
        if (isCurrent()) {
          busyRef.current = false;
          setBusy(false);
        }
      }
    }
    if (!projectId) return false;
    const hourIds = (entries || []).map((entry) => entry?.id).filter((id) => id != null);
    attemptSelection.current = selectionKey;
    attempt.current += 1;
    const myAttempt = attempt.current;
    const isCurrent = () => mounted.current && attempt.current === myAttempt;
    setFeedback(null);
    if (hourIds.length > PROJECT_HOURS_PDF_MAX_IDS) {
      const error = new Error("too many");
      error.code = "PROJECT_HOURS_PDF_TOO_MANY_CLIENT";
      setFeedback(projectHoursPdfFeedback(error, ui));
      return false;
    }
    busyRef.current = true;
    setBusy(true);
    try {
      const fallbackFilename = projectHoursPdfFallbackFilename({
        prefix: ui("pdf_filename_prefix", "timeoversikt"), projectNumber, projectId, dateFrom, dateTo,
      });
      const { blob, filename } = await requestProjectHoursPdf(projectId, { hourIds, dateFrom, dateTo, language, fallbackFilename });
      // The file contains exactly the hours the user exported, so it is still
      // saved after a selection change; only stale feedback/busy state is dropped.
      saveBlobAsFile(blob, filename);
      return true;
    } catch (error) {
      if (isCurrent()) setFeedback(projectHoursPdfFeedback(error, ui));
      return false;
    } finally {
      if (isCurrent()) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }, [companyScope, exportFilters, projectId, projectNumber, entries, dateFrom, dateTo, language, ui, selectionKey]);

  const clearFeedback = useCallback(() => setFeedback(null), []);

  return { exportPdf, busy, feedback, clearFeedback };
}
